import type React from 'react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminEmailsApi } from '@api/admin-extras';
import type { EmailType } from '@api/types';

interface Props {
  type: EmailType;
  /** Label legible que mostramos en el header del modal junto al subject. */
  label: string;
  onClose: () => void;
}

type Tab = 'html' | 'text';

/**
 * Modal de previsualización de plantillas de email.
 *
 * - Carga `GET /api/admin/system/emails/preview/:type` con React Query.
 * - El HTML se renderiza dentro de un `<iframe sandbox="">` (sin scripts,
 *   sin same-origin) para neutralizar cualquier markup ejecutable que pudiera
 *   colarse en la plantilla.
 * - El texto plano se muestra con `<pre>` + `white-space: pre-wrap`.
 * - Cierre con click fuera del panel o ESC (patrón de `Modal` reutilizable,
 *   replicado inline porque necesitamos un layout custom con tabs).
 */
export function EmailPreviewModal({ type, label, onClose }: Props): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('html');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['email-preview', type],
    queryFn: () => adminEmailsApi.preview(type),
    // El modal solo monta cuando el usuario abre la preview — siempre fetch.
    staleTime: 0,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  function onScrim(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="modal-scrim"
      onClick={onScrim}
      role="dialog"
      aria-modal="true"
      aria-label={`Vista previa: ${label}`}
    >
      <div className="modal" data-wide="true">
        <div className="modal-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="modal-title">Vista previa: {label}</h2>
            <p className="page-sub" style={{ margin: '4px 0 0' }}>
              {data?.subject ? <><strong>Asunto:</strong> {data.subject}</> : 'Cargando plantilla…'}
            </p>
          </div>
          <button
            className="icon-btn"
            data-variant="ghost"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="seg" style={{ alignSelf: 'flex-start' }}>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'html'}
              onClick={() => { setTab('html'); }}
            >
              HTML
            </button>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'text'}
              onClick={() => { setTab('text'); }}
            >
              Texto plano
            </button>
          </div>

          {isLoading && (
            <div style={{ padding: 32, color: 'var(--fg-muted)', textAlign: 'center' }}>
              <span className="spinner" /> Cargando vista previa…
            </div>
          )}

          {isError && (
            <div style={{ padding: 16, color: 'var(--danger)', fontSize: 13 }}>
              No se pudo cargar la vista previa.
              {error instanceof Error && error.message ? <> ({error.message})</> : null}
            </div>
          )}

          {data && tab === 'html' && (
            <iframe
              title={`Vista previa HTML: ${label}`}
              sandbox=""
              srcDoc={data.html}
              style={{
                width: '100%',
                minHeight: 500,
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: '#fff',
              }}
            />
          )}

          {data && tab === 'text' && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 13,
                padding: 16,
                maxHeight: 500,
                overflow: 'auto',
                background: 'var(--bg-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                margin: 0,
              }}
            >
              {data.text}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
